import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { SubmitButton } from "@/components/ui/submit-button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { useTabErrorNavigation } from "@/hooks/useTabErrorNavigation";
import { cn, errorHandler } from "@/lib/utils";
import { accessLevelService } from "@/services/api/accessLevel";
import { userService } from "@/services/api/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft, ChevronRight, Lock, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import {
  FieldErrors,
  useFieldArray,
  useForm,
  type Resolver,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import UserFormSkeleton from "../Users/UserFormSkeleton";
import AccessLevelDataTab from "./AccessLevelDataTab";
import AccessLevelModulesTab from "./AccessLevelModulesTab";

const formSchema = z.object({
  access_level_data: z.object({
    name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
    description: z.string().nullable(),
    is_default: z.boolean().default(false),
  }),
  modules: z
    .array(
      z.object({
        module_id: z.number(),
        permission: z.number(),
        module_title: z.string(),
        max_permission: z.number(),
        parent_id: z.number().nullable(),
      }),
    )
    .optional(),
});

export type AccessLevelFormValues = z.infer<typeof formSchema>;

const STEPS = [
  {
    id: "access_level_data",
    label: "Dados do perfil",
    icon: Lock,
    priority: 1,
  },
  {
    id: "modules",
    label: "Módulos",
    icon: Settings,
    priority: 2,
  },
];

type StepId = (typeof STEPS)[number]["id"];

const TAB_FIELDS_MAPPING: Record<
  StepId,
  readonly ["access_level_data"] | readonly ["modules"]
> = {
  access_level_data: ["access_level_data"],
  modules: ["modules"],
};

type AccessLevelSubmitPayload = {
  name: string;
  description: string | null;
  is_default: boolean;
  modules: {
    module_id: number;
    permission: number;
  }[];
};

type ApiModule = {
  id: number;
  name: string;
  max_permission: number;
  parent_id: number | null;
};

const AccessLevelForm = () => {
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [currentStep, setCurrentStep] = useState<StepId>("access_level_data");
  const { id } = useParams();
  const navigate = useNavigate();

  const form = useForm<AccessLevelFormValues>({
    // ponytail: resolvers 5 types the raw input apart from the parsed
    // output; the form keeps the parsed type, as it did on resolvers 3.
    resolver: zodResolver(formSchema) as Resolver<AccessLevelFormValues>,
    shouldFocusError: true,
    defaultValues: {
      access_level_data: {
        name: "",
        description: null,
        is_default: false,
      },
      modules: [],
    },
  });

  const { navigateToFirstTab } = useTabErrorNavigation<AccessLevelFormValues>({
    errors: form.formState.errors,
    tabFieldsMapping: TAB_FIELDS_MAPPING,
    currentTab: currentStep,
    setCurrentTab: setCurrentStep,
    tabs: STEPS,
  });

  const { fields: moduleFields } = useFieldArray({
    control: form.control,
    name: "modules",
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        if (id) {
          const accessLevelResponse = await accessLevelService.show(Number(id));
          const modulesResponse = await accessLevelService.getModules(
            Number(id),
            1,
          );

          const moduleData = modulesResponse.data.map((m) => ({
            module_id: m.module_id,
            permission: m.permission,
            module_title: m.module_title,
            max_permission: m.max_permission,
            parent_id: m.parent_id,
          }));

          form.reset({
            access_level_data: {
              name: accessLevelResponse.data.name,
              description: accessLevelResponse.data.description,
              is_default: Boolean(accessLevelResponse.data.is_default),
            },
            modules: moduleData,
          });
        } else {
          const responseModules = await userService.getModules(0, 1);

          const apiModules = responseModules.data as unknown as ApiModule[];
          const moduleData = apiModules.map((module) => ({
            module_id: module.id,
            permission: 0,
            module_title: module.name,
            max_permission: module.max_permission,
            parent_id: module.parent_id,
          }));

          form.reset({
            access_level_data: {
              name: "",
              description: null,
              is_default: false,
            },
            modules: moduleData,
          });
        }
      } catch (err) {
        errorHandler(err, "Erro ao carregar perfil de acesso");
        navigate("/access-levels");
      }
    };

    loadData().finally(() => setShowSkeleton(false));
  }, [id, form, navigate]);

  const { handleSubmit, loading } = useFormSubmit<AccessLevelSubmitPayload>({
    id,
    createService: accessLevelService.create as (
      data: AccessLevelSubmitPayload,
    ) => Promise<unknown>,
    updateService: (updateId, data) =>
      accessLevelService.update(Number(updateId), data),
    onSuccess: () => {
      navigate("/access-levels");
    },
  });

  const onSubmit = async (values: AccessLevelFormValues) => {
    try {
      const payload = {
        name: values.access_level_data.name,
        description: values.access_level_data.description,
        is_default: values.access_level_data.is_default,
        modules: (values.modules || []).map((module) => ({
          module_id: module.module_id,
          permission: module.permission,
        })),
      };

      await handleSubmit(payload);
    } catch (err) {
      errorHandler(err, "Erro ao salvar perfil de acesso");
      const firstTab = navigateToFirstTab();
      if (firstTab && firstTab !== currentStep) {
        setTimeout(() => setCurrentStep(firstTab), 100);
      }
    }
  };

  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const goToNextStep = () => {
    if (!isLastStep) {
      const next = STEPS[currentStepIndex + 1].id as StepId;
      setCurrentStep(next);
    }
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      const prev = STEPS[currentStepIndex - 1].id as StepId;
      setCurrentStep(prev);
    }
  };

  if (showSkeleton) {
    return <UserFormSkeleton />;
  }

  return (
    <div className="container mx-auto py-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader className="p-0">
              <Tabs
                value={currentStep}
                onValueChange={(value) => setCurrentStep(value as StepId)}
                className="w-full"
              >
                <TabsList className="flex w-full h-auto justify-start rounded-none border-b border-border bg-transparent p-0">
                  {STEPS.map((step) => {
                    const stepId = step.id as StepId;
                    const tabKey = TAB_FIELDS_MAPPING[stepId][0];
                    const errorsAny = form.formState
                      .errors as FieldErrors<AccessLevelFormValues>;
                    const hasError =
                      (tabKey === "access_level_data" &&
                        errorsAny.access_level_data) ||
                      (tabKey === "modules" && errorsAny.modules);

                    return (
                      <TabsTrigger
                        key={step.id}
                        value={step.id}
                        className={cn(
                          "relative flex h-12 items-center justify-center px-4 font-semibold shadow-none transition-none",
                          "rounded-t-md rounded-b-none border border-transparent -mb-px",
                          "data-[state=active]:z-10 data-[state=active]:bg-card data-[state=active]:border-border data-[state=active]:border-b-transparent data-[state=active]:text-foreground",
                          hasError && "text-destructive",
                        )}
                      >
                        <step.icon className="mr-2 h-4 w-4" />
                        {step.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                <CardContent className="p-6">
                  <AccessLevelDataTab form={form} />
                  <AccessLevelModulesTab
                    form={form}
                    fields={moduleFields}
                    isAdmin={
                      form.getValues("access_level_data.name").toLowerCase() ===
                      "admin"
                    }
                  />
                </CardContent>
              </Tabs>
            </CardHeader>
          </Card>

          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={goToPreviousStep}
                disabled={isFirstStep}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Anterior
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={goToNextStep}
                disabled={isLastStep}
              >
                Próximo
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            <div className="flex space-x-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/access-levels")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AccessLevelForm;
